import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, noop } from 'lodash-es';
import Button from 'reactstrap/lib/Button';

import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { UserProfileModel } from 'client/data/models/profile/profile';

import { signIn } from 'client/actions/profile';
import { PROFILE_SCREEN } from 'site-modules/shared/components/profile/profile-screen';

// HOCs
import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';
import { profileScreenMethods } from 'site-modules/shared/components/profile/profile-screen-methods';

import { ForgotPasswordAsync } from 'site-modules/shared/components/profile/forgot-password/forgot-password-async';
import { SignEmailForm } from './sign-email-form/sign-email-form';
import { SignPasswordForm } from './sign-password-form/sign-password-form';
import { SocialSignIn } from './social-sign-in/social-sign-in';
import { SMART_SIGN_SCREEN } from './helper';

// Smart Sign Step 1 and Step 2, Social Login (Very first step).
function SmartSignComponent({
  dataIdm = {},
  createOrUpdateIdmData,
  updateIdmDataOnLogin,
  profileDataEmail = null,
  profileScreen = '',
  isVerifyEmail = false,
  isAuthenticateModal = false,
  isAuthenticated = null,
  isAnonymousUser = false,
  setModelValue,
  closeAuthenticatedModal = noop,
  signInAction,
  toggleForgotPassword,
}) {
  const [currentScreen, setCurrentScreen] = useState(SMART_SIGN_SCREEN.SIGN);
  const [isSignInAction, setSignInAction] = useState(false);
  const [profileEmail, setProfileEmail] = useState('');

  function signCompleted() {
    signInAction();
    closeAuthenticatedModal();
  }

  function onStepOneSuccess({ emailValue, nextScreen }) {
    setModelValue('signInEmail', UserProfileModel, emailValue);
    setProfileEmail(emailValue);
    setCurrentScreen(nextScreen);
  }

  function handleBackAction() {
    setCurrentScreen(SMART_SIGN_SCREEN.SIGN);
    setSignInAction(false);
  }

  function handleToggleForgotPassword(event) {
    toggleForgotPassword(event);
    handleBackAction();
  }

  function handleSignInAction() {
    setSignInAction(true);
  }

  if (profileScreen === PROFILE_SCREEN.FORGOT_PASSWORD_MODAL && isAuthenticateModal) {
    return <ForgotPasswordAsync />;
  }

  return (
    <div className="authenticated-smart-sign smart-sign text-gray-darker">
      {currentScreen === SMART_SIGN_SCREEN.SIGN && (
        <Fragment>
          <SignEmailForm
            successCallback={onStepOneSuccess}
            isAuthenticated={isAuthenticated}
            profileDataEmail={profileDataEmail}
            isVerifyEmail={isVerifyEmail}
            isSignInAction={isSignInAction}
          />
          {!isVerifyEmail && !isSignInAction && (
            <Fragment>
              <div className="text-center my-1_5">or</div>
              <div className="px-2_5 px-sm-3_5">
                <SocialSignIn
                  dataIdm={dataIdm}
                  updateIdmDataOnLogin={updateIdmDataOnLogin}
                  signCompleted={signCompleted}
                />
              </div>
              <div className="px-2_5 px-sm-3_5 mt-1 text-center">
                <Button
                  id="smart-sign-in-email-submit"
                  type="submit"
                  color="outline-blue-50"
                  className="auth-submit-button fw-normal"
                  block
                  data-tracking-id="submit_sign_up_email"
                  data-no-refresh
                  onClick={handleSignInAction}
                >
                  <i className="icon-envelop3 align-middle me-0_5" aria-hidden />
                  Sign In with Email
                </Button>
              </div>
              <div className="mt-1_5 size-12 text-gray-darker d-flex align-items-center">
                <i className="icon-shield-check pe-0_25" aria-hidden />
                <span>We&apos;ll never post anything without your permission.</span>
              </div>
            </Fragment>
          )}
        </Fragment>
      )}
      {[SMART_SIGN_SCREEN.SIGN_UP, SMART_SIGN_SCREEN.SIGN_IN].includes(currentScreen) && (
        <SignPasswordForm
          profileEmail={profileEmail}
          isVerifyEmail={isVerifyEmail}
          isAnonymousUser={isAnonymousUser}
          dataIdm={dataIdm}
          createOrUpdateIdmData={createOrUpdateIdmData}
          updateIdmDataOnLogin={updateIdmDataOnLogin}
          closeAuthenticatedModal={closeAuthenticatedModal}
          toggleForgotPassword={handleToggleForgotPassword}
          signCompleted={signCompleted}
          backAction={handleBackAction}
          isSignInAction={currentScreen === SMART_SIGN_SCREEN.SIGN_IN}
        />
      )}
    </div>
  );
}

SmartSignComponent.propTypes = {
  setModelValue: PropTypes.func.isRequired,
  createOrUpdateIdmData: PropTypes.func.isRequired,
  updateIdmDataOnLogin: PropTypes.func.isRequired,
  signInAction: PropTypes.func.isRequired,
  toggleForgotPassword: PropTypes.func.isRequired,
  dataIdm: PropTypes.shape({}),
  isAuthenticated: PropTypes.bool,
  isAnonymousUser: PropTypes.bool,
  profileDataEmail: PropTypes.string,
  profileScreen: PropTypes.string,
  isVerifyEmail: PropTypes.bool,
  closeAuthenticatedModal: PropTypes.func,
  isAuthenticateModal: PropTypes.bool,
};

export const mapStateToProps = ({ profile }) => ({
  isAuthenticated: profile.isAuthenticated,
  isAnonymousUser: profile.isAnonymousUser,
  profileDataEmail: get(
    profile,
    'data.idm.identifiers.email[0]',
    get(profile, 'signInEmail', get(profile, 'options.email'))
  ),
  profileScreen: profile.screen,
});

const mapDispatchToProps = dispatch => ({
  signInAction: () => dispatch(signIn(false)),
});

export const SmartSign = connect(
  mapStateToProps,
  mapDispatchToProps
)(
  connectToModel(insiderMethods(profileScreenMethods(SmartSignComponent)), {
    dataIdm: bindToPath(props => (props.isAuthenticated ? 'data.idm' : ''), UserProfileModel),
  })
);
